import React, { useState, useEffect } from 'react';
import { Button, Space, message, Popconfirm, Input, Tag, Form } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SafetyCertificateOutlined, ReloadOutlined, LockOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText, ProFormSelect } from '@ant-design/pro-components';
import { renderDateTime, ResizableTable } from './utils';

interface Role {
  id: number;
  role_name: string;
  permission: number[];
  is_default: number;
  create_time: string;
}

const permissionNames: Record<number, string> = {
  1: '仪表盘',
  2: '招标信息管理',
  3: '订单中心',
  4: '信息发布',
  5: '账号管理',
  6: '后台管理',
  7: '下载中心',
  8: '开标大厅',
};

export default function SystemRoles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Role | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/roles', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setRoles(resp.data || []);
      } else {
        message.error(resp?.error || '获取角色列表失败');
      }
    } catch (err) {
      message.error('获取角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Role) => {
    if (record.is_default) {
      message.warning('默认角色不能修改');
      return;
    }
    setEditingItem(record);
    form.setFieldsValue({
      role_name: record.role_name,
      permission: record.permission || [],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/roles/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchRoles();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingItem 
        ? `/api/admin/roles/${editingItem.id}`
        : '/api/admin/roles';
      const method = editingItem ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingItem ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchRoles();
        return true;
      } else {
        message.error(resp?.error || '操作失败');
        return false;
      }
    } catch (err) {
      message.error('操作失败');
      return false;
    }
  };

  // 过滤数据
  const filteredData = roles.filter(r => 
    r.role_name?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Role> = [
    {
      title: '角色名称',
      dataIndex: 'role_name',
      key: 'role_name',
      width: 200,
      render: (text, record) => (
        <Space>
          <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
          {record.is_default === 1 && (
            <Tag icon={<LockOutlined />} color="warning">默认</Tag>
          )}
        </Space>
      )
    },
    {
      title: '权限',
      dataIndex: 'permission',
      key: 'permission',
      render: (perms: number[], record) => (
        <Space wrap>
          {perms?.map(p => (
            <Tag key={p} color="blue" style={{ borderRadius: 12 }}>
              {record?.role_name === '财务人员' && p === 3 ? '订单管理' : (permissionNames[p] || p)}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: renderDateTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            className={`admin-action-btn ${record.is_default === 1 ? '' : 'edit-btn'}`}
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.is_default === 1}
          >
            编辑
          </Button>
          {record.is_default === 1 ? (
            <Button 
              size="small" 
              className="admin-action-btn"
              disabled 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          ) : (
            <Popconfirm
              title="确定要删除这个角色吗？"
              description="删除后将无法恢复"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button 
                size="small"
                className="admin-action-btn delete-btn"
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <SafetyCertificateOutlined />
          </div>
          <div>
            <h2>角色管理</h2>
            <p>管理系统角色和权限</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索角色..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchRoles}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增角色
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <ResizableTable
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SafetyCertificateOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingItem ? '编辑角色' : '新增角色'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
        submitter={{
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="role_name"
            label="角色名称"
            placeholder="请输入角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
          />
          <ProFormSelect
            name="permission"
            label="权限"
            placeholder="请选择权限"
            mode="multiple"
            options={Object.entries(permissionNames).map(([key, value]) => ({
              label: value,
              value: parseInt(key),
            }))}
            rules={[{ required: true, message: '请选择权限' }]}
          />
        </div>
      </ModalForm>
    </div>
  );
}

